/* eslint-disable react-hooks/set-state-in-effect -- Reset and sync state from reactive property subscriptions */
import { useContext, useEffect, useState } from 'react';
import { useCustomSelector } from '../../../redux/store';
import { RendererContext } from '../../../contexts/RendererContext';
import { ClipMethods } from '../../../types/graphqlTypes';
import { PropertyType } from '../../../modules/Properties';
import { useReactivePropertyManager } from '../../../modules/Properties/reactive/useReactivePropertyManager';
import { ensureFilterTogglePropertyRegistered } from '../../../modules/Properties/integration/annotations/FilterToggleProperty';
import { ensureClipMethodPropertyRegistered } from '@pointorama/potree-shared';
import { GradientType } from '../../../modules/Properties/types';

const TOGGLE_OBJECT_ID = 'toggles';

export const useHeatmapLegendOverlay = (projectId: string) => {
  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const service = useReactivePropertyManager({ projectId });

  // Get the first selected heatmap-capable object: a plane annotation OR a surface
  // group. Both expose the same heatmap reactive properties keyed by their identifier.
  const matchesPlane = (id: string) => viewer?.scene.planes?.some((plane) => plane.identifier === id);
  const matchesSurface = (id: string) => viewer?.scene.surfaces?.some((surface) => surface.identifier === id);
  const selectedPlaneAnnotation = selectedAnnotations?.find((annotation) => matchesPlane(annotation.identifier) || matchesSurface(annotation.identifier));
  // Surfaces store tolerance under surface_tolerance (PROJECT_GLOBAL); planes under plane_tolerance (PROJECT_USER).
  const selectedIsSurface = selectedPlaneAnnotation ? matchesSurface(selectedPlaneAnnotation.identifier) : false;

  // Subscribe to reactive properties
  const [isFilterEnabled, setIsFilterEnabled] = useState(false);
  const [isLegendEnabled, setIsLegendEnabled] = useState(false);
  const [clipMethod, setClipMethod] = useState<string>('ISOLATE');
  const [gradient, setGradient] = useState<GradientType>('SPECTRAL');
  const [tolerance, setTolerance] = useState(0.01);

  useEffect(() => {
    if (!selectedPlaneAnnotation) {
      setIsFilterEnabled(false);
      setIsLegendEnabled(false);
      setClipMethod('ISOLATE');
      setGradient('SPECTRAL');
      setTolerance(0.01);
      return;
    }

    const id = selectedPlaneAnnotation.identifier;
    ensureFilterTogglePropertyRegistered(id);
    ensureClipMethodPropertyRegistered(id);

    const filterProperty = service.getProperty(`filter.${id}`, TOGGLE_OBJECT_ID, PropertyType.PROJECT_USER);
    const legendProperty = service.getProperty('heatmap_legend', id, PropertyType.PROJECT_USER);
    const clipMethodProperty = service.getProperty(`clip_method.${id}`, 'filters', PropertyType.PROJECT_USER);
    const gradientProperty = service.getProperty('heatmap_gradient', id, PropertyType.PROJECT_USER);
    const toleranceProperty = selectedIsSurface
      ? service.getProperty('surface_tolerance', id, PropertyType.PROJECT_GLOBAL)
      : service.getProperty('plane_tolerance', id, PropertyType.PROJECT_USER);

    setIsFilterEnabled(filterProperty.value === true);
    setIsLegendEnabled(legendProperty.value !== false);
    setClipMethod((clipMethodProperty.value as string) || 'ISOLATE');
    setGradient((gradientProperty.value as GradientType) || 'SPECTRAL');
    setTolerance((toleranceProperty.value as number) || 0.01);

    const subs = [
      filterProperty.subscribe((value) => setIsFilterEnabled(value === true)),
      legendProperty.subscribe((value) => setIsLegendEnabled(value !== false)),
      clipMethodProperty.subscribe((value) => setClipMethod((value as string) || 'ISOLATE')),
      gradientProperty.subscribe((value) => setGradient((value as GradientType) || 'SPECTRAL')),
      toleranceProperty.subscribe((value) => setTolerance((value as number) || 0.01)),
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, [selectedPlaneAnnotation, selectedIsSurface, service]);

  // Check if the plane has heatmap clip method enabled and legend property is enabled
  const hasHeatmapClipMethodEnabled = selectedPlaneAnnotation ? isFilterEnabled && clipMethod === ClipMethods.Heatmap : false;

  // Check if we have exactly one plane/surface selected with heatmap clip method AND filter enabled
  const planeAnnotations = selectedAnnotations?.filter((annotation) => matchesPlane(annotation.identifier) || matchesSurface(annotation.identifier)) || [];

  // The overlay should be visible when all conditions are met
  const overlayVisible = Boolean(selectedPlaneAnnotation && planeAnnotations.length === 1 && hasHeatmapClipMethodEnabled && isLegendEnabled);

  return {
    overlayVisible,
    planeIdentifier: selectedPlaneAnnotation?.identifier,
    gradient,
    tolerance,
    hasSinglePlaneWithHeatmapAndFilter: overlayVisible,
  };
};
